import React from 'react';

import { Avatar, AvatarGroup, Box, Button, Stack, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';

import { OfferCategory, OfferRequirements } from '../../../../graphql/API';
import { useVenueGetVerificationUrl } from '../../../../hooks/reactQuery/balance/useVenueGetVerificationUrl';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import useVenueId from '../../../../hooks/useVenueId';
import {
  fetchAccountBalance,
  handleBalanceIsLoading,
} from '../../../../redux/features/balance/balanceSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import Icons from '../../../../themes/icons';
import { delay } from '../../../../utils/delay';
import { centsToDollars, formatNumberWithCommas } from '../../../../utils/formatter';
import { VENUE_MANUAL_TAGS } from '../../../../utils/venue-tags';
import LoadingButton from '../../../Button/LoadingButton';
import AddBalanceDialog from '../../../Dialogs/AddBalance/AddBalanceDialog';
import { AddFundsStripeDialog } from '../../../Dialogs/AddFundsStripe/AddFundsStripeDialog';
import { useCreateInvite } from '../../../Dialogs/CreateInvite/context/CreateInvite.context';
import {
  EventStep,
  ExtendedOfferRequirements,
  Step,
} from '../../../Dialogs/CreateInvite/context/types';
import HowCollabsWorksDialog from '../../../Dialogs/HowCollabsWorks/HowCollabsWorksDialog';
import StripeAccountVerifyDialog from '../../../Dialogs/StripeAccountVerify/StripeAccountVerifyDialog';
import { InviteState } from '../components/InviteState';

const RequirementIcon: Record<string, React.ReactNode> = {
  [OfferRequirements.postOnInstagramStories]: (
    <Icons.Content1StoryIcon style={{ width: '30px', height: '30px' }} />
  ),
  [OfferRequirements.postOnInstagramStories3x]: (
    <Icons.Content1StoryIcon style={{ width: '30px', height: '30px' }} />
  ),
  [OfferRequirements.postOnInstagramStories2x]: (
    <Icons.Content1StoryIcon style={{ width: '30px', height: '30px' }} />
  ),
  [OfferRequirements.postOnInstagramFeed]: (
    <Icons.ContentFeedIcon style={{ width: '30px', height: '30px' }} />
  ),
  [OfferRequirements.postOnInstagramReels]: (
    <Icons.ContentReelIcon style={{ width: '30px', height: '30px' }} />
  ),
  [OfferRequirements.postOnTikTokVideo]: (
    <Icons.TiktokIcon style={{ width: '21px', height: '21px' }} />
  ),
  [ExtendedOfferRequirements.noRequirement]: <Icons.GiftingIcon />,
};

const RequirementTitle: Record<string, string> = {
  [OfferRequirements.postOnInstagramStories]: '1 Story',
  [OfferRequirements.postOnInstagramStories3x]: '3 Story',
  [OfferRequirements.postOnInstagramStories2x]: '2 Stories',
  [OfferRequirements.postOnInstagramFeed]: 'Feed',
  [OfferRequirements.postOnInstagramReels]: 'Reel',
  [OfferRequirements.postOnTikTokVideo]: 'TikTok video',
  [ExtendedOfferRequirements.noRequirement]: 'Gifting',
};

export const Confirmation: React.FC = () => {
  const { isCompact } = useBreakpoints();
  const { enqueueSnackbar } = useSnackbar();

  const venueID = useVenueId();
  const appDispatch = useAppDispatch();
  const { value: balance, isLoading: balanceIsLoading } = useAppSelector((state) => state.balance);
  const subscription = useAppSelector((state) => state.subscription.value);
  const isPlusPlan = subscription?.plan?.includes('plus');
  const venueState = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const { state, submitForm, dispatch, mutation } = useCreateInvite();

  const { showModal } = useModal();

  const [successAmount, setSuccessAmount] = React.useState(0);
  const [totalAmountWithFee, setTotalAmountWithFee] = React.useState(0);

  const { data: verficationData } = useVenueGetVerificationUrl(
    { venueID: venueID || '' },
    balance?.status === 'inactive',
  );

  const [stripeDialog, setStripeDialog] = React.useState(false);

  const handleStripeDialog = () => {
    setStripeDialog((prev) => !prev);
  };

  const totalBalance = React.useMemo(
    () => centsToDollars(balance?.balance || 0),
    [balance?.balance],
  );

  const totalOfferPrice = React.useMemo(
    () =>
      state.usersWithOfferPrice?.reduce<number>((acc, item) => acc + (item.offerPrice || 0), 0) ||
      0,
    [state.usersWithOfferPrice],
  );

  const handleSubmit = () => {
    if (state.offer?.category === OfferCategory.event) {
      dispatch({
        type: 'update',
        payload: {
          usersWithOfferPrice: state.usersWithOfferPrice?.map((user) => ({
            userID: Number(user.userID),
            offerPrice: user.offerPrice || 0,
          })),
          previousTotalBalance: totalBalance,
        },
      });
      return submitForm();
    } else {
      if (totalOfferPrice > Number(totalBalance)) {
        return enqueueSnackbar('Your balance is insufficient', { variant: 'warning' });
      }

      const isValid = (state.usersWithOfferPrice || []).length === state.users.length;
      const minPriceRequired = isPlusPlan ? 0 : 1;
      const isPriceValid = (state.usersWithOfferPrice || []).every(
        (user) => {
          const price = user.offerPrice ?? 0;
          return price >= minPriceRequired && price >= 0;
        },
      );

      if (!isValid) {
        return enqueueSnackbar('Please give offer price to users', { variant: 'warning' });
      }

      if (!isPriceValid) {
        const message = isPlusPlan
          ? 'Offer price cannot be negative'
          : 'Offer price should be greater than 0';
        return enqueueSnackbar(message, { variant: 'warning' });
      }

      submitForm();
    }
  };

  const handleAddFundsDialog = () => {
    if (balance?.status === 'inactive') {
      return showModal(StripeAccountVerifyDialog, {
        onVerify: () => {
          return window.open(verficationData?.onBoardingURL, '_self');
        },
      });
    }

    showModal(AddBalanceDialog, {
      onSubmit: (totalAmountWithFee) => {
        setTotalAmountWithFee(totalAmountWithFee as number);
        handleStripeDialog();
      },
      getOriginalPriceHandle: (amount) => {
        setSuccessAmount(amount as number);
      },
    });
  };

  const handleContinue = () => {
    if (
      (state.offer?.category !== OfferCategory.event &&
        state.offer?.category !== OfferCategory.product &&
        state.step < Step.Summary) ||
      ((state.offer?.category === OfferCategory.event ||
        state.offer?.category === OfferCategory.product) &&
        state.step < Step.RequirementSelection)
    ) {
      dispatch({
        type: 'next',
        payload: {},
      });
    } else {
      handleSubmit();
    }
  };

  const isEnterPriceIsGreater = React.useMemo(
    () => totalOfferPrice > Number(totalBalance),
    [totalBalance, totalOfferPrice],
  );

  const handleHowCollabsWorksDialog = () => {
    showModal(HowCollabsWorksDialog, {});
  };

  const handleChangeOffer = () => {
    dispatch({
      type: 'update',
      payload: {
        offer: undefined,
        step: Step.OfferSelection,
        ...((state.offer?.category === OfferCategory.event ||
          state.offer?.category === OfferCategory.product) && { dates: [] }),
      },
    });
  };

  const handleChangeDates = () => {
    dispatch({ type: 'update', payload: { dates: undefined, step: Step.DateSelection } });
  };

  const handleChangeRequirements = () => {
    dispatch({
      type: 'update',
      payload: {
        requirements: undefined,
        step: [OfferCategory.event, OfferCategory.product].includes(
          state.offer?.category as OfferCategory,
        )
          ? 2
          : 3,
      },
    });
    dispatch({ type: 'prev' });
  };

  // On a narrow panel the "Change" button competes with the product title for
  // the same row, which is what forced titles like "Camicia notte estiva" to
  // wrap over three lines. Stack it underneath instead and shrink it.
  const summaryRowSx = isCompact
    ? ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        flex: 1,
        minWidth: 0,
      } as const)
    : ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
        minWidth: 0,
      } as const);

  const changeButtonSx = {
    border: '1px solid #F1F2F1',
    ':hover': { border: '1px solid #F1F2F1' },
    ...(isCompact
      ? {
          fontSize: '12px',
          minWidth: 0,
          height: 28,
          px: 1,
          py: 0,
          '& .MuiButton-startIcon': { ml: 0, mr: 0.5, '& > *': { fontSize: 14 } },
        }
      : { fontSize: '13px' }),
  };

  const summaryStep = React.useMemo(
    () =>
      [OfferCategory.event, OfferCategory.product].includes(state.offer?.category as OfferCategory)
        ? 2
        : 3,
    [state],
  );

  return (
    <Box
      sx={{
        borderLeft: '1px solid #F1F2F1',
        padding: isCompact ? '24px 20px' : '28px 42px',
        height: 'calc(100vh - 65px)',
      }}
    >
      <Stack spacing={3}>
        <Container>
          <SectionOne>
            <InviteState
              active={state.step === 0 || state.step > 0}
              title='Select users to invite'
              requirementComponent={
                <Box mx={1} display={'flex'} alignItems={'center'} gap={'14px'}>
                  <AvatarGroup spacing='small'>
                    {state.users.slice(0, 3).map((user) => (
                      <Avatar
                        alt={user.firstname}
                        src={user?.picture?.thumbnail || ''}
                        sx={{ height: '32px', width: '32px' }}
                      />
                    ))}
                  </AvatarGroup>
                  {state.users.length > 3 && (
                    <Typography component={'span'} fontSize={'13px'}>
                      {state.users.length} users
                    </Typography>
                  )}
                </Box>
              }
            />
            <InviteState
              active={!!state.offer}
              title={isOnlineBrand ? 'Select a product' : 'Select an offer'}
              requirementComponent={
                <Box sx={{ ...summaryRowSx, mx: 1 }}>
                  {state.offer && (
                    <>
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        gap={'14px'}
                        sx={{ minWidth: 0, maxWidth: '100%' }}
                      >
                        <Avatar
                          sx={{ borderRadius: '4px', flexShrink: 0 }}
                          src={state.offer?.picture?.thumbnail || ''}
                          alt={state.offer?.title || 'offer'}
                        />
                        <Typography fontFamily={'Poppins'} fontSize={'13px'} noWrap>
                          {state.offer?.title}
                        </Typography>
                      </Box>
                      {!mutation.isSuccess && (
                        <Button
                          startIcon={<Icons.ChangeIcon />}
                          variant='outlined'
                          color='primary'
                          sx={changeButtonSx}
                          disabled={state.step === Step.OfferSelection}
                          onClick={handleChangeOffer}
                        >
                          Change
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              }
            />
            {state.offer?.category !== OfferCategory.event &&
              state.offer?.category !== OfferCategory.product &&
              !isOnlineBrand && (
                <InviteState
                  active={(state.dates?.length || 0) > 0}
                  title='Select dates'
                  requirementComponent={
                    <Box sx={{ ...summaryRowSx, mx: 1, mt: 2 }}>
                      {(state.dates?.length || 0) > 0 && (
                        <>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontFamily={'Poppins'} fontSize={'13px'}>
                              {(state.dates?.length || 0) > 1 && `${state.dates?.length} Dates`}
                              {(state.dates?.length || 0) === 1 &&
                                state.dates?.[0]?.format('dddd MMMM D, YYYY')}
                            </Typography>
                            <Typography fontFamily={'Poppins'} fontSize={'13px'}>
                              {state.startTime?.format('hh:mm A')} -{' '}
                              {state.endTime?.format('hh:mm A')}
                            </Typography>
                          </Box>
                          {!mutation.isSuccess && (
                            <Button
                              startIcon={<Icons.ChangeIcon />}
                              variant='outlined'
                              color='primary'
                              sx={changeButtonSx}
                              disabled={state.step <= Step.DateSelection}
                              onClick={handleChangeDates}
                            >
                              Change
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  }
                />
              )}

            <InviteState
              title='Select Requirement'
              active={!!state.requirements}
              requirementComponent={
                <Box sx={{ ...summaryRowSx, mx: 1, mt: 2 }}>
                  {!!state.requirements && (
                    <>
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        gap={1}
                        sx={{ minWidth: 0, maxWidth: '100%' }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            backgroundColor: '#FCFCFC',
                            border: '1px solid #f1f2f1',
                            padding: '10px',
                            flexShrink: 0,
                          }}
                        >
                          {RequirementIcon[state.requirements]}
                        </Box>
                        <Typography fontSize={'13px'} fontFamily={'Poppins'} noWrap>
                          {RequirementTitle[state.requirements]}
                        </Typography>
                      </Box>
                      {!mutation.isSuccess && (
                        <Button
                          startIcon={<Icons.ChangeIcon />}
                          variant='outlined'
                          color='primary'
                          sx={changeButtonSx}
                          onClick={handleChangeRequirements}
                          disabled={
                            ((state.offer?.category === OfferCategory.event ||
                              state.offer?.category === OfferCategory.product) &&
                              state.step <= 1) ||
                            (state.offer?.category !== OfferCategory.event &&
                              state.offer?.category !== OfferCategory.product &&
                              state.step <= 2)
                          }
                        >
                          Change
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              }
            />
            <InviteState title='Checkout' active={mutation.isSuccess} />
            {mutation.isSuccess && (
              <Typography fontFamily={'Poppins'} fontWeight={500} mx={4}>
                Total ${totalOfferPrice}
              </Typography>
            )}

            {((state.offer?.category !== OfferCategory.event &&
              state.offer?.category !== OfferCategory.product &&
              state.step === Step.Summary) ||
              ((state.offer?.category === OfferCategory.event ||
                state.offer?.category === OfferCategory.product) &&
                state.step === 2)) && (
              <Box
                sx={{
                  borderRadius: '8px',
                  border: '1px solid #F1F2F1',
                  backgroundColor: '#FCFCFC',
                  my: 2,
                  p: '14px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography fontSize={'15px'} color='#848484' fontFamily={'Poppins'}>
                    Balance
                  </Typography>
                  <Typography fontSize={'15px'} color='#848484' fontFamily={'Poppins'}>
                    ${formatNumberWithCommas(+totalBalance)}
                  </Typography>
                </Box>
                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography
                    fontSize={'15px'}
                    color='#111'
                    fontFamily={'Poppins'}
                    fontWeight={500}
                  >
                    Total cost
                  </Typography>
                  <Typography
                    fontSize={'15px'}
                    color='#111'
                    fontFamily={'Poppins'}
                    fontWeight={500}
                  >
                    ${formatNumberWithCommas(totalOfferPrice)}
                  </Typography>
                </Box>
              </Box>
            )}
          </SectionOne>
          {(state.offer?.category !== OfferCategory.event &&
            state.offer?.category !== OfferCategory.product &&
            state.step === Step.Success) ||
          ((state.offer?.category === OfferCategory.event ||
            state.offer?.category === OfferCategory.product) &&
            state.step === 3) ? null : (
            <SectionTwo>
              {(!balanceIsLoading && balance?.status === 'inactive') ||
              ((Number(totalBalance) <= 0 || isEnterPriceIsGreater) && state.offer?.category !== OfferCategory.event) ? (
                <Button
                  fullWidth
                  variant='contained'
                  color='primary'
                  sx={{ py: 1.5 }}
                  startIcon={<Icons.BalanceIcon />}
                  onClick={handleAddFundsDialog}
                >
                  +Add Funds
                </Button>
              ) : (
                <LoadingButton
                  fullWidth
                  loading={mutation.isPending}
                  variant='contained'
                  color='primary'
                  sx={{ py: 1.5 }}
                  disabled={
                    (state.step === Step.OfferSelection && !state.offer) ||
                    (state.step === Step.DateSelection &&
                      (state.dates?.length || 0) === 0 &&
                      ![OfferCategory.event, OfferCategory.product].includes(
                        state.offer?.category as OfferCategory,
                      )) ||
                    (state.step === summaryStep &&
                      Number(totalBalance) <= 0 &&
                      state.offer?.category !== OfferCategory.event) ||
                    (state.step === summaryStep && !state.offerPricesValid) ||
                    (state.step === summaryStep && isEnterPriceIsGreater) ||
                    state.users.length === 0
                  }
                  onClick={handleContinue}
                >
                  {(![OfferCategory.event, OfferCategory.product].includes(
                    state.offer?.category as OfferCategory,
                  ) &&
                    state.step === Step.Summary) ||
                  ((state.offer?.category === OfferCategory.event ||
                    state.offer?.category === OfferCategory.product) &&
                    state.step === 2)
                    ? 'Confirm and Invite'
                    : 'Continue'}
                </LoadingButton>
              )}
            </SectionTwo>
          )}
        </Container>

        {((state.offer?.category !== OfferCategory.event &&
          state.offer?.category !== OfferCategory.product &&
          state.step === Step.Summary) ||
          ((state.offer?.category === OfferCategory.event ||
            state.offer?.category === OfferCategory.product) &&
            state.step === 2)) && (
          <LearnMoreContainer>
            <Icons.InfoLightIcon style={{ width: '24px', height: '24px' }} />
            <Box display={'flex'} flexDirection={'column'} gap={2}>
              <Typography fontFamily={'Poppins'} fontSize={'13px'}>
                Your payment will be held for 72 hours If the invitees do not accept your
                collaboration, you will receive your money back.
              </Typography>
              <LearnMoreBtn href='#' onClick={handleHowCollabsWorksDialog}>
                Learn more
              </LearnMoreBtn>
            </Box>
          </LearnMoreContainer>
        )}
      </Stack>

      <AddFundsStripeDialog
        open={stripeDialog}
        onClose={handleStripeDialog}
        amount={totalAmountWithFee}
        amountWithoutFee={successAmount}
        onSuccess={() => {
          appDispatch(handleBalanceIsLoading());
          delay(10000).then(() => {
            appDispatch(fetchAccountBalance({ venueID: venueID || '' }));
          });
        }}
      />
    </Box>
  );
};

const Container = styled.div`
  border-radius: 12px;
  border: 1px solid #f1f2f1;
`;

const SectionOne = styled.div`
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTwo = styled.div`
  border-top: 1px solid #f1f2f1;
  padding: 18px 22px;
`;

const LearnMoreContainer = styled.div`
  border-radius: 12px;
  border: 1px solid #f1f2f1;
  padding: 20px;

  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

const LearnMoreBtn = styled.a`
  font-size: 15px;
  font-weight: 500;
  color: #000;
  &:hover {
    color: #000;
  }
`;
